import { useState } from 'react';
import { useLocation as useUrlLocation } from 'react-router-dom-v5-compat';

import { useEffectOnce } from 'utils/customHooks/useEffectOnce';

interface ParamKeyDefinition<V extends string = string, P extends string = string> {
  variableName: V;
  paramKey: P;
}

type QueryParamsFrom<T extends readonly ParamKeyDefinition[]> = {
  [K in T[number] as K['variableName']]: string | undefined;
};

const getQueryParams = <const T extends readonly ParamKeyDefinition[]>(
  search: string,
  paramKeys: T,
): QueryParamsFrom<T> => {
  const queryParams = new URLSearchParams(search);
  const result = {} as QueryParamsFrom<T>;
  for (const param of paramKeys) {
    (result as Record<string, string | undefined>)[param.variableName] =
      queryParams.get(param.paramKey) ?? undefined;
  }
  return result;
};

export const useTemporaryQueryParams = <const T extends readonly ParamKeyDefinition[]>(
  paramKeys: T,
): QueryParamsFrom<T> => {
  const { search } = useUrlLocation();

  const [queryParams] = useState<QueryParamsFrom<T>>(getQueryParams(search, paramKeys));

  useEffectOnce(() => {
    const urlSearchParams = new URLSearchParams(search);
    for (const param of paramKeys) {
      urlSearchParams.delete(param.paramKey);
    }

    const searchParamsString = (() => {
      if (!urlSearchParams.size) {
        return '';
      }

      return `?${urlSearchParams.toString()}`;
    })();

    window.history.replaceState(null, '', `${window.location.pathname}${searchParamsString}`);
  });

  return queryParams;
};
